/**
 * ConditionAuditPanel — iter 30
 *
 * Audits all Shopify products for Google Merchant "condition" compliance
 * (refurbished / used / new). Detects mismatches between text and metafield,
 * missing warranty / grade / accessories disclosures, and offers 1-click fix
 * to update the google.condition metafield.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Recycle, CircleNotch, Warning, ShieldCheck, ShieldWarning,
  ArrowSquareOut, Wrench, ListChecks, Eye, X,
} from "@phosphor-icons/react";
import { conditionAudit, conditionFix, conditionAutoFix, conditionBulkAutoFix } from "../lib/api";

/** Normalize any axios error (string | array of pydantic validation objects |
 *  object | undefined) into a single readable string for toast.error. */
function formatApiError(err, fallback = "Erreur") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return err?.message || fallback;
}

const SEV_BADGE = {
  critical: "bg-rose-700 text-white",
  warning: "bg-amber-500 text-neutral-900",
  ok: "bg-emerald-600 text-white",
};

const CONDITION_LABEL = {
  refurbished: "Reconditionné",
  used: "Occasion",
  new: "Neuf",
  unknown: "Inconnu",
};

export function ConditionAuditPanel({ activeConn }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [maxProducts, setMaxProducts] = useState(100);
  const [fixingId, setFixingId] = useState(null);
  const [autoFixingId, setAutoFixingId] = useState(null);
  const [warrantyMonths, setWarrantyMonths] = useState(36);
  const [autoFixLang, setAutoFixLang] = useState("fr");
  const [bulkFixing, setBulkFixing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {total, done}
  const [bulkPreview, setBulkPreview] = useState(null); // {severity, results: [...]}

  const runAudit = async () => {
    if (!activeConn) {
      toast.error("Connectez d'abord une boutique Shopify");
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const res = await conditionAudit({
        connection_id: activeConn.id,
        max_products: maxProducts,
      });
      setReport(res);
      if (res.products_with_issues === 0) {
        toast.success(`Aucun produit problématique sur ${res.total_products_scanned} scannés 🎉`);
      } else {
        toast.warning(
          `${res.critical} critique(s), ${res.warnings} avertissement(s) sur ${res.total_products_scanned} produits`
        );
      }
    } catch (err) {
      toast.error(formatApiError(err, "Erreur audit conditions"));
    } finally {
      setLoading(false);
    }
  };

  const applyFix = async (issue) => {
    if (!issue.suggested_metafield) return;
    setFixingId(issue.product_id);
    try {
      const res = await conditionFix({
        connection_id: activeConn.id,
        product_id: issue.product_id,
        new_condition: issue.suggested_metafield,
        dry_run: false,
      });
      toast.success(
        `Metafield mis à jour : ${res.previous_value || "vide"} → ${res.new_value}`
      );
      // Mark the issue as fixed locally
      setReport((prev) => ({
        ...prev,
        issues: prev.issues.map((i) =>
          i.product_id === issue.product_id
            ? {
                ...i,
                declared_condition: res.new_value,
                mismatch: false,
                severity: i.warranty_mentioned && i.title_has_keyword ? "ok" : "warning",
                issues: i.issues.filter((msg) => !msg.startsWith("INCOHÉRENCE")),
                suggested_metafield: null,
              }
            : i
        ),
        critical: prev.critical - (issue.severity === "critical" ? 1 : 0),
      }));
    } catch (err) {
      toast.error(formatApiError(err, "Erreur fix"));
    } finally {
      setFixingId(null);
    }
  };

  const applyAutoFix = async (issue) => {
    const target = issue.suggested_metafield || issue.text_implied_condition;
    if (!target || target === "unknown") {
      toast.error("Condition non détectée — impossible de lancer l'auto-fix");
      return;
    }
    setAutoFixingId(issue.product_id);
    try {
      const res = await conditionAutoFix({
        connection_id: activeConn.id,
        product_id: issue.product_id,
        target_condition: target,
        language: autoFixLang,
        warranty_months: warrantyMonths,
        dry_run: false,
      });
      toast.success(res.message || "Auto-fix appliqué");
      // Mark issue as fully fixed locally
      setReport((prev) => ({
        ...prev,
        issues: prev.issues.map((i) =>
          i.product_id === issue.product_id
            ? {
                ...i,
                declared_condition: res.metafield_new,
                product_title: res.title_new || i.product_title,
                mismatch: false,
                title_has_keyword: true,
                warranty_mentioned: i.warranty_mentioned || res.body_warranty_injected,
                accessories_mentioned: i.accessories_mentioned || res.body_accessories_injected,
                severity: "ok",
                issues: ["✓ Auto-fix appliqué : " + (res.message || "OK")],
                suggested_metafield: null,
              }
            : i
        ),
        critical: prev.critical - (issue.severity === "critical" ? 1 : 0),
        warnings: prev.warnings - (issue.severity === "warning" ? 1 : 0),
      }));
    } catch (err) {
      toast.error(formatApiError(err, "Erreur auto-fix"));
    } finally {
      setAutoFixingId(null);
    }
  };

  const _runBulk = async (candidates, dryRun, severityLabel) => {
    if (!candidates?.length) return null;
    setBulkFixing(true);
    setBulkProgress({ total: candidates.length, done: 0 });
    try {
      const res = await conditionBulkAutoFix({
        connection_id: activeConn.id,
        items: candidates.map((c) => ({
          product_id: c.product_id,
          target_condition: c.text_implied_condition,
        })),
        language: autoFixLang,
        warranty_months: warrantyMonths,
        dry_run: dryRun,
      });
      return res;
    } catch (err) {
      toast.error(formatApiError(err, `Erreur ${dryRun ? "aperçu" : "bulk auto-fix"}`));
      return null;
    } finally {
      setBulkFixing(false);
      setBulkProgress(null);
    }
  };

  const _eligibleCandidates = (filterSeverity) => {
    if (!report?.issues?.length) return [];
    return report.issues.filter(
      (i) => i.severity === filterSeverity
        && (i.text_implied_condition === "refurbished" || i.text_implied_condition === "used")
    );
  };

  const previewBulkAutoFix = async (filterSeverity = "critical") => {
    const candidates = _eligibleCandidates(filterSeverity);
    if (!candidates.length) {
      toast.error(`Aucun produit ${filterSeverity} éligible à l'aperçu`);
      return;
    }
    const res = await _runBulk(candidates, true, filterSeverity);
    if (!res) return;
    // Merge backend result with the original issue title (for diff display)
    const enriched = res.results.map((r) => {
      const src = candidates.find((c) => c.product_id === r.product_id) || {};
      return { ...r, original_title: src.product_title || "" };
    });
    setBulkPreview({
      severity: filterSeverity,
      results: enriched,
      total: res.total,
      succeeded: res.succeeded,
      failed: res.failed,
    });
  };

  const confirmBulkAutoFixFromPreview = async () => {
    if (!bulkPreview?.results?.length) return;
    // Keep only the candidates that the dry-run did not fail on
    const okIds = new Set(bulkPreview.results.filter((r) => r.ok).map((r) => r.product_id));
    const candidates = _eligibleCandidates(bulkPreview.severity).filter(
      (c) => okIds.has(c.product_id)
    );
    setBulkPreview(null);
    if (!candidates.length) {
      toast.error("Aucun produit valide à appliquer après l'aperçu");
      return;
    }
    const res = await _runBulk(candidates, false, bulkPreview.severity);
    if (!res) return;
    _applyBulkResultToState(res, bulkPreview.severity);
  };

  const _applyBulkResultToState = (res, severityLabel) => {
    const fixed_ids = new Set(res.results.filter((r) => r.ok).map((r) => r.product_id));
    setReport((prev) => ({
      ...prev,
      issues: prev.issues.map((i) => {
        if (!fixed_ids.has(i.product_id)) return i;
        const result = res.results.find((r) => r.product_id === i.product_id)?.result;
        return {
          ...i,
          declared_condition: result?.metafield_new || i.declared_condition,
          product_title: result?.title_new || i.product_title,
          mismatch: false,
          title_has_keyword: true,
          warranty_mentioned: i.warranty_mentioned || result?.body_warranty_injected,
          accessories_mentioned: i.accessories_mentioned || result?.body_accessories_injected,
          severity: "ok",
          issues: ["✓ Auto-fix bulk appliqué"],
          suggested_metafield: null,
        };
      }),
      critical: severityLabel === "critical"
        ? Math.max(0, prev.critical - res.succeeded)
        : prev.critical,
      warnings: severityLabel === "warning"
        ? Math.max(0, prev.warnings - res.succeeded)
        : prev.warnings,
    }));
    if (res.failed === 0) {
      toast.success(`✓ Bulk auto-fix : ${res.succeeded}/${res.total} produits corrigés`);
    } else {
      toast.warning(
        `${res.succeeded}/${res.total} corrigés, ${res.failed} échec(s). Détails dans la console.`
      );
      // eslint-disable-next-line no-console
      console.warn("bulk_auto_fix failures:", res.results.filter((r) => !r.ok));
    }
  };

  const applyBulkAutoFix = async (filterSeverity = "critical") => {
    const candidates = _eligibleCandidates(filterSeverity);
    if (!candidates.length) {
      toast.error(`Aucun produit ${filterSeverity} éligible à l'auto-fix`);
      return;
    }
    if (!window.confirm(
      `Appliquer l'auto-fix complet sur ${candidates.length} produit(s) ${filterSeverity}s ? ` +
      `Cela modifiera leurs titres, descriptions et metafield google.condition.`
    )) return;
    const res = await _runBulk(candidates, false, filterSeverity);
    if (!res) return;
    _applyBulkResultToState(res, filterSeverity);
  };

  if (!activeConn) return null;

  return (
    <section
      className="border border-neutral-900 bg-white"
      data-testid="shopify-condition-panel"
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-neutral-900 bg-rose-50">
        <Recycle size={18} weight="fill" className="text-rose-700 flex-shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-rose-900">
            Audit Conditions Reconditionné / Occasion
          </div>
          <div className="text-[11px] font-mono text-neutral-700 mt-0.5">
            Détecte les produits où le texte dit « reconditionné/occasion » mais le metafield Google est « new » (cause #1 de suspension Google).
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] uppercase tracking-[0.18em] font-mono text-neutral-600">
            Langue auto-fix
          </label>
          <select
            value={autoFixLang}
            onChange={(e) => setAutoFixLang(e.target.value)}
            className="h-9 border border-neutral-900 px-2 text-xs font-mono focus:outline-none"
            data-testid="condition-autofix-lang"
            disabled={loading}
          >
            {[["fr", "FR"], ["en", "EN"], ["es", "ES"], ["it", "IT"], ["pt", "PT"], ["de", "DE"]].map(
              ([code, label]) => (
                <option key={code} value={code}>{label}</option>
              )
            )}
          </select>
          <label className="text-[10px] uppercase tracking-[0.18em] font-mono text-neutral-600">
            Garantie (mois)
          </label>
          <select
            value={warrantyMonths}
            onChange={(e) => setWarrantyMonths(parseInt(e.target.value, 10))}
            className="h-9 border border-neutral-900 px-2 text-xs font-mono focus:outline-none"
            data-testid="condition-warranty-months"
            disabled={loading}
          >
            {[6, 12, 18, 24, 36, 60].map((n) => (
              <option key={n} value={n}>{n >= 12 && n % 12 === 0 ? `${n} (${n / 12} ans)` : n}</option>
            ))}
          </select>
          <label className="text-[10px] uppercase tracking-[0.18em] font-mono text-neutral-600">
            Max produits
          </label>
          <select
            value={maxProducts}
            onChange={(e) => setMaxProducts(parseInt(e.target.value, 10))}
            className="h-9 border border-neutral-900 px-2 text-xs font-mono focus:outline-none"
            data-testid="condition-max-products"
            disabled={loading}
          >
            {[50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={runAudit}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
            data-testid="condition-audit-button"
          >
            {loading ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <ListChecks size={14} weight="fill" />
            )}
            {loading ? "Audit en cours…" : "Lancer l'audit"}
          </button>
        </div>
      </div>

      {report && (
        <div className="p-4 space-y-3" data-testid="condition-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <Stat label="Produits scannés" value={report.total_products_scanned} />
            <Stat label="Avec problèmes" value={report.products_with_issues}
                  tone={report.products_with_issues > 0 ? "warn" : "ok"} />
            <Stat label="Critiques" value={report.critical}
                  tone={report.critical > 0 ? "crit" : "ok"} />
            <Stat label="Avertissements" value={report.warnings}
                  tone={report.warnings > 0 ? "warn" : "ok"} />
          </div>

          {report.issues.length === 0 ? (
            <div
              className="flex items-center gap-3 border border-emerald-700 bg-emerald-50 px-4 py-4"
              data-testid="condition-no-issues"
            >
              <ShieldCheck size={20} weight="fill" className="text-emerald-700" />
              <div className="text-sm font-mono text-emerald-900">
                Tous les produits sont conformes — aucun mismatch détecté entre le texte et le metafield <code>google.condition</code>.
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="condition-issues-list">
              {(report.critical > 0 || report.warnings > 0) && (
                <div
                  className="flex flex-wrap items-center gap-3 border border-emerald-700 bg-emerald-50 px-4 py-3"
                  data-testid="condition-bulk-bar"
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono font-bold text-emerald-900">
                      ♻️ Auto-Fix Bulk — corriger tous les critiques en 1 clic
                    </div>
                    <div className="text-[10px] font-mono text-emerald-800 mt-0.5">
                      Met à jour metafield + injecte mot-clé titre, garantie {warrantyMonths >= 12 && warrantyMonths % 12 === 0 ? `${warrantyMonths / 12} an(s)` : `${warrantyMonths}m`} et &laquo; Tous les accessoires d&apos;origine sont disponibles &raquo; sur tous les produits {report.critical > 0 ? "critiques" : "à problème"} d&apos;un coup.
                    </div>
                  </div>
                  {report.critical > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => previewBulkAutoFix("critical")}
                        disabled={bulkFixing || loading}
                        className="inline-flex items-center gap-2 border border-emerald-700 bg-white text-emerald-900 hover:bg-emerald-100 disabled:opacity-60 px-3 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
                        data-testid="condition-bulk-preview-critical-button"
                        title="Simulation sans modification — voir ce qui serait corrigé sur chaque produit"
                      >
                        {bulkFixing ? (
                          <CircleNotch size={14} className="animate-spin" />
                        ) : (
                          <Eye size={14} weight="fill" />
                        )}
                        Aperçu
                      </button>
                      <button
                        type="button"
                        onClick={() => applyBulkAutoFix("critical")}
                        disabled={bulkFixing || loading}
                        className="inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
                        data-testid="condition-bulk-fix-critical-button"
                      >
                        {bulkFixing ? (
                          <CircleNotch size={14} className="animate-spin" />
                        ) : (
                          <Wrench size={14} weight="fill" />
                        )}
                        {bulkFixing
                          ? `En cours… ${bulkProgress?.done || 0}/${bulkProgress?.total || 0}`
                          : `Corriger les ${report.critical} critiques`}
                      </button>
                    </>
                  )}
                  {report.warnings > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => previewBulkAutoFix("warning")}
                        disabled={bulkFixing || loading}
                        className="inline-flex items-center gap-2 border border-amber-600 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60 px-3 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
                        data-testid="condition-bulk-preview-warning-button"
                        title="Simulation sans modification — voir ce qui serait corrigé sur les avertissements"
                      >
                        <Eye size={14} />
                        Aperçu
                      </button>
                      <button
                        type="button"
                        onClick={() => applyBulkAutoFix("warning")}
                        disabled={bulkFixing || loading}
                        className="inline-flex items-center gap-2 border border-emerald-700 bg-white text-emerald-900 hover:bg-emerald-100 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
                        data-testid="condition-bulk-fix-warning-button"
                      >
                        <Wrench size={14} />
                        {`Corriger les ${report.warnings} avertissements`}
                      </button>
                    </>
                  )}
                </div>
              )}
              {report.issues.map((iss) => (
                <ConditionIssueRow
                  key={iss.product_id}
                  issue={iss}
                  storeUrl={activeConn.store_url}
                  onFix={applyFix}
                  fixing={fixingId === iss.product_id}
                  onAutoFix={applyAutoFix}
                  autoFixing={autoFixingId === iss.product_id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {!report && !loading && (
        <div className="p-6 text-center text-xs font-mono text-neutral-500">
          Cliquez sur <strong>Lancer l&apos;audit</strong> pour scanner vos produits Shopify et détecter les mismatchs de conditions.
        </div>
      )}

      {bulkPreview && (
        <BulkPreviewModal
          preview={bulkPreview}
          warrantyMonths={warrantyMonths}
          lang={autoFixLang}
          onClose={() => setBulkPreview(null)}
          onConfirm={confirmBulkAutoFixFromPreview}
          confirming={bulkFixing}
        />
      )}
    </section>
  );
}

function BulkPreviewModal({ preview, warrantyMonths, lang, onClose, onConfirm, confirming }) {
  const okResults = preview.results.filter((r) => r.ok);
  const failedResults = preview.results.filter((r) => !r.ok);
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4"
      data-testid="condition-bulk-preview-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-white border border-neutral-900 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-neutral-900 bg-violet-50">
          <Eye size={22} weight="fill" className="text-violet-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-violet-900">
              Aperçu — aucune modification appliquée
            </div>
            <div className="text-[11px] font-mono text-neutral-700 mt-0.5">
              Voici exactement ce qui sera modifié sur chaque produit {preview.severity}. Garantie injectée : <strong>{warrantyMonths >= 12 && warrantyMonths % 12 === 0 ? `${warrantyMonths / 12} an(s)` : `${warrantyMonths} mois`}</strong> · Langue : <strong>{lang.toUpperCase()}</strong> · Total : <strong>{preview.total}</strong> ({preview.succeeded} OK, {preview.failed} échec(s))
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-violet-100 ma-trans"
            data-testid="condition-bulk-preview-close"
            aria-label="Fermer l'aperçu"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-2">
          {okResults.length === 0 && failedResults.length === 0 && (
            <div className="text-xs font-mono text-neutral-500 text-center py-6">
              Aucun résultat à afficher.
            </div>
          )}
          {okResults.map((r) => (
            <PreviewRow key={r.product_id} result={r} />
          ))}
          {failedResults.length > 0 && (
            <div className="mt-3 border border-rose-300 bg-rose-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-mono font-bold text-rose-900 mb-1">
                {failedResults.length} échec(s) — ces produits seront ignorés
              </div>
              {failedResults.map((r) => (
                <div key={r.product_id} className="text-[11px] font-mono text-rose-800">
                  ✕ Produit #{r.product_id} — {r.error || "erreur inconnue"}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-neutral-900 bg-neutral-50">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="border border-neutral-700 bg-white text-neutral-900 hover:bg-neutral-100 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
            data-testid="condition-bulk-preview-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming || okResults.length === 0}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
            data-testid="condition-bulk-preview-confirm"
          >
            {confirming ? <CircleNotch size={14} className="animate-spin" /> : <Wrench size={14} weight="fill" />}
            {confirming ? "Application…" : `Confirmer et appliquer (${okResults.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ result }) {
  const r = result.result || {};
  const titleChanged = r.title_updated && r.title_previous !== r.title_new;
  return (
    <div
      className="border border-neutral-300 bg-white px-3 py-2"
      data-testid={`condition-bulk-preview-row-${result.product_id}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <span className="text-[10px] font-mono text-neutral-500 mt-0.5">#{result.product_id}</span>
        <div className="flex-1 min-w-0">
          {titleChanged ? (
            <>
              <div className="text-[11px] font-mono text-neutral-500 line-through">{r.title_previous}</div>
              <div className="text-[12px] font-mono text-neutral-900 font-medium">{r.title_new}</div>
            </>
          ) : (
            <div className="text-[12px] font-mono text-neutral-900">{r.title_new || r.title_previous || "(sans titre)"}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
        {r.metafield_updated && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-600 text-emerald-800 bg-emerald-50">
            metafield: {r.metafield_previous || "∅"} → {r.metafield_new}
          </span>
        )}
        {titleChanged && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-600 text-emerald-800 bg-emerald-50">
            titre + mot-clé
          </span>
        )}
        {r.body_warranty_injected && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-600 text-emerald-800 bg-emerald-50">
            garantie injectée
          </span>
        )}
        {r.body_accessories_injected && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-600 text-emerald-800 bg-emerald-50">
            accessoires injectés
          </span>
        )}
        {r.body_ean_injected && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-600 text-emerald-800 bg-emerald-50" title={r.ean_source_url || ""}>
            EAN {r.ean_value} injecté{r.ean_source ? ` (${r.ean_source})` : ""}
          </span>
        )}
        {r.ean_warning && !r.body_ean_injected && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-rose-500 text-rose-800 bg-rose-50"
            title={r.ean_warning}
            data-testid={`condition-bulk-preview-ean-missing-${result.product_id}`}
          >
            ⚠ EAN à compléter manuellement
          </span>
        )}
        {!r.metafield_updated && !titleChanged && !r.body_warranty_injected && !r.body_accessories_injected && !r.body_ean_injected && (
          <span className="text-neutral-500">Rien à corriger</span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const cls = {
    crit: "bg-rose-50 border-rose-300 text-rose-900",
    warn: "bg-amber-50 border-amber-300 text-amber-900",
    ok: "bg-emerald-50 border-emerald-300 text-emerald-900",
    neutral: "bg-neutral-50 border-neutral-300 text-neutral-900",
  }[tone];
  return (
    <div className={`border px-3 py-2 ${cls}`}>
      <div className="text-2xl font-bold font-mono">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono mt-0.5">
        {label}
      </div>
    </div>
  );
}

function ConditionIssueRow({ issue, storeUrl, onFix, fixing, onAutoFix, autoFixing }) {
  const sevCls = SEV_BADGE[issue.severity] || SEV_BADGE.warning;
  const productUrl = `https://${storeUrl}/admin/products/${issue.product_id}`;
  const canAutoFix = (
    issue.text_implied_condition === "refurbished" ||
    issue.text_implied_condition === "used"
  ) && issue.severity !== "ok";
  return (
    <div
      className="border border-neutral-900 bg-white"
      data-testid={`condition-issue-${issue.product_id}`}
    >
      <div className="flex flex-wrap items-start gap-3 px-3 py-2 border-b border-neutral-200">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono font-bold ${sevCls}`}
        >
          {issue.severity === "critical" ? <Warning size={11} weight="fill" /> : <ShieldWarning size={11} weight="fill" />}
          {issue.severity}
        </span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium text-neutral-900">
            {issue.product_title}
          </div>
          <div className="text-[10px] font-mono text-neutral-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
            <span>Texte → <strong>{CONDITION_LABEL[issue.text_implied_condition]}</strong></span>
            <span>Metafield → <strong className={issue.mismatch ? "text-rose-700" : ""}>{CONDITION_LABEL[issue.declared_condition || "unknown"]}</strong></span>
            {issue.warranty_duration && (
              <span>Garantie : <strong>{issue.warranty_duration}</strong></span>
            )}
          </div>
        </div>
        <a
          href={productUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 border border-neutral-300 px-2 py-1 text-[10px] uppercase tracking-widest font-mono text-neutral-700 hover:bg-neutral-100 ma-trans"
          data-testid={`condition-open-product-${issue.product_id}`}
        >
          <ArrowSquareOut size={10} />
          Shopify Admin
        </a>
      </div>
      <ul className="px-3 py-2 space-y-1 text-[12px] font-mono text-neutral-700">
        {issue.issues.map((msg, idx) => (
          <li key={idx} className="flex gap-2">
            <span className="text-rose-700 flex-shrink-0">▸</span>
            <span>{msg}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-neutral-200 bg-neutral-50">
        <div className="text-[10px] font-mono text-neutral-600 flex flex-wrap gap-x-3 gap-y-1">
          <Pill label="Titre OK" ok={issue.title_has_keyword} />
          <Pill label="Garantie" ok={issue.warranty_mentioned} />
          <Pill label="Grade" ok={issue.grade_mentioned} />
          <Pill label="Accessoires" ok={issue.accessories_mentioned} />
        </div>
        {issue.suggested_metafield && (
          <button
            type="button"
            onClick={() => onFix(issue)}
            disabled={fixing || autoFixing}
            className="inline-flex items-center gap-1.5 border border-neutral-700 bg-white text-neutral-900 hover:bg-neutral-100 disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-widest font-medium ma-trans"
            data-testid={`condition-fix-${issue.product_id}`}
            title="Met à jour uniquement le metafield google.condition"
          >
            {fixing ? <CircleNotch size={11} className="animate-spin" /> : <Wrench size={11} weight="fill" />}
            {fixing
              ? "Mise à jour…"
              : `Fix metafield → ${CONDITION_LABEL[issue.suggested_metafield]}`}
          </button>
        )}
        {canAutoFix && (
          <button
            type="button"
            onClick={() => onAutoFix(issue)}
            disabled={fixing || autoFixing}
            className="ml-auto inline-flex items-center gap-1.5 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-widest font-medium ma-trans"
            data-testid={`condition-autofix-${issue.product_id}`}
            title="Met à jour metafield + ajoute Reconditionné dans le titre + injecte garantie + accessoires d'origine"
          >
            {autoFixing ? <CircleNotch size={11} className="animate-spin" /> : <Wrench size={11} weight="fill" />}
            {autoFixing ? "Auto-fix…" : "♻️ Auto-Fix complet"}
          </button>
        )}
      </div>
    </div>
  );
}

function Pill({ label, ok }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${
        ok
          ? "border-emerald-600 text-emerald-700 bg-emerald-50"
          : "border-rose-600 text-rose-700 bg-rose-50"
      }`}
    >
      {ok ? "✓" : "✗"} {label}
    </span>
  );
}

export default ConditionAuditPanel;
